'use client';
import React from 'react';

export default function HeroSection() {
  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#111111' }}
    >
      {/* Background image overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1800&q=80')`,
          opacity: 0.18,
        }}
        aria-hidden="true"
      />

      {/* Orange diagonal accent - PwC style */}
      <div
        className="absolute right-0 bottom-0 w-1/2 h-full pointer-events-none"
        aria-hidden="true"
        style={{
          background: 'linear-gradient(135deg, transparent 60%, rgba(253,81,8,0.12) 100%)',
        }}
      />
      <div
        className="absolute right-16 bottom-0 w-64 h-64 pointer-events-none"
        aria-hidden="true"
        style={{
          background: 'linear-gradient(135deg, transparent 40%, rgba(253,81,8,0.2) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-site mx-auto px-4 sm:px-6 lg:px-12 pt-20 sm:pt-24 pb-16 sm:pb-20 w-full">
        <div className="max-w-2xl">
          {/* Eyebrow */}
          <p
            className="text-primary font-sans text-xs sm:text-sm font-semibold tracking-widest uppercase mb-4 sm:mb-6"
            style={{ letterSpacing: '3px' }}
          >
            Freelance Financial Services
          </p>

          {/* Main heading */}
          <h1
            className="text-white mb-5 sm:mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(32px, 8vw, 64px)',
              fontWeight: 400,
              lineHeight: '1.12',
              letterSpacing: '-1.5px',
            }}
          >
            Precise Books.
            <br />
            <span className="font-bold">Clear Financials.</span>
            <br />
            Confident Decisions.
          </h1>

          {/* Subtext */}
          <p
            className="text-white/75 text-base sm:text-lg mb-8 sm:mb-10 max-w-lg"
            style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", lineHeight: '1.7' }}
          >
            SYHJ delivers professional bookkeeping, bank reconciliation, and financial statement preparation — built on CMA-level rigor, tailored for small businesses and startups.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <button
              onClick={() => handleScroll('#estimate')}
              className="inline-block bg-primary text-white font-bold px-8 py-4 text-base transition-all duration-300 hover:bg-primary-dark text-center touch-manipulation"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)', minHeight: '52px' }}
            >
              Get a Free Estimate
            </button>
            <button
              onClick={() => handleScroll('#services')}
              className="inline-block text-white font-bold px-8 py-4 text-base border border-white/40 transition-all duration-300 hover:border-white hover:bg-white/10 text-center touch-manipulation"
              style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif", transitionTimingFunction: 'cubic-bezier(0.22, 0.61, 0.36, 1)', minHeight: '52px' }}
            >
              Explore Services
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap gap-6 sm:gap-8 mt-10 sm:mt-14 pt-8 sm:pt-10 border-t border-white/15">
            {[
              { value: 'US CMA', label: 'Candidate (Part 2 Cleared)' },
              { value: '3', label: 'Core Services Offered' },
              { value: '100%', label: 'Secure Client Portal' },
            ].map((stat) => (
              <div key={stat.label}>
                <div
                  className="text-white font-bold text-xl sm:text-2xl"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-white/55 text-xs sm:text-sm mt-1"
                  style={{ fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif" }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/40 text-xs font-sans tracking-widest uppercase" style={{ letterSpacing: '2px' }}>Scroll</span>
        <svg width="16" height="24" viewBox="0 0 16 24" fill="none" className="text-white/40">
          <path d="M8 4v16M2 14l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </section>
  );
}
